import { useAction, useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { useCallback, useEffect, useRef } from 'react'
import { usePreviousDistinct } from 'react-use'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button, DotsLoader, Refresh2Icon } from '@blade/ui-kit'
import { isAdminAtom } from '@root/entities/auth'
import { DATE_FORMAT } from '@root/shared/core'
import { useTranslation } from '@root/shared/localization'
import { PushPinIcon } from '@root/shared/ui-kit'
import { FadeOverflowVirtuozo } from '@root/shared/ui-kit/utils/fade-overflow-virtuozo'

import { supportDialogTypingAtom } from '../atoms'

import { SupportConversationItem } from './support-conversation-item'
import { SupportConversationMessage } from './support-conversation-message'
import { SupportConversationSkeletons } from './support-conversation-skeletons'
import { SupportDialogHeading } from './support-dialog-heading'
import { SupportDialogHeadingAdmin } from './support-dialog-heading-admin'

import type { SupportTicket } from '../model/support-ticket'
import type { VirtuosoHandle } from 'react-virtuoso'

export type SupportConversationMode = 'user' | 'support'

type SupportConversationProps = {
  ticket: SupportTicket
  onClose?: () => void
}

export const SupportConversation: React.FC<SupportConversationProps> = ({
  ticket,
  onClose,
}) => {
  const { t } = useTranslation()
  const [typing] = useAtom(supportDialogTypingAtom)
  const [messagesState] = useAtom(ticket.messages)
  const prevMessagesState = usePreviousDistinct(messagesState)
  const refresh = useAction(ticket.messages.run)
  const sendMessage = useAction(ticket.sendMessage)
  const [loadMoreState] = useAtom(ticket.messages.loadMoreState)
  const [hasMore] = useAtom(ticket.messages.hasMore)
  const loadMore = useAction(ticket.messages.loadMore)
  const scrollerRef = useRef<HTMLElement>(null)
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const [isAdmin] = useAtom(isAdminAtom)

  const scrollRestoreRef = useRef<null | {
    prevScrollHeight: number
    prevScrollTop: number
  }>(null)
  const prevLastMessageIdRef = useRef<string | undefined>(undefined)

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({
        index: 'LAST',
        behavior: 'smooth',
        align: 'end',
      })
    })
  }, [])

  useEffect(() => {
    prevLastMessageIdRef.current = undefined
  }, [ticket.id])

  useEffect(() => {
    if (!AsyncStateN.isSuccess(messagesState)) {
      return
    }
    const messages = messagesState.value.data
    if (messages.length === 0) {
      return
    }
    const lastId = messages.at(-1)!.id
    const prevLastId = prevLastMessageIdRef.current

    if (prevLastId === undefined) {
      prevLastMessageIdRef.current = lastId
      scrollToBottom()
      return
    }
    if (prevLastId === lastId) {
      return
    }
    prevLastMessageIdRef.current = lastId
    scrollToBottom()
  }, [messagesState, scrollToBottom])

  const handleMessageSend = (message: string) => {
    sendMessage(message)
  }
  const handleAtTopStateChange = (atTop: boolean) => {
    if (!atTop) {
      return
    }
    if (
      AsyncStateN.isLoading(loadMoreState) ||
      AsyncStateN.isError(loadMoreState) ||
      !hasMore
    ) {
      return
    }
    const container = scrollerRef.current
    if (container) {
      scrollRestoreRef.current = {
        prevScrollHeight: container.scrollHeight,
        prevScrollTop: container.scrollTop,
      }
    }
    loadMore()
  }
  const handleRetry = () => refresh()

  const handleListRendered = () => {
    if (!prevMessagesState) {
      return
    }

    const container = scrollerRef.current
    const snapshot = scrollRestoreRef.current
    if (!container || !snapshot) {
      return
    }

    if (AsyncStateN.isSuccess(messagesState)) {
      const nextScrollHeight = container.scrollHeight
      const heightDiff = nextScrollHeight - snapshot.prevScrollHeight

      virtuosoRef.current?.scrollTo({
        top: heightDiff,
        behavior: 'instant',
      })
    }
  }

  const renderMessages = () =>
    AsyncStateN.match(messagesState, {
      NotInitiated: () => <Empty>{t('support.empty-messages')}</Empty>,
      Loading: () => <SupportConversationSkeletons />,
      Success: ({ data: messages }) => {
        if (messages.length === 0) {
          return <Empty>{t('support.empty-messages')}</Empty>
        }

        return (
          <FadeOverflowVirtuozo
            data={messages}
            virtuosoRef={virtuosoRef}
            itemContent={(_idx, message) => (
              <SupportConversationItem
                key={message.id}
                message={message}
                author={ticket.author}
              />
            )}
            scrollerRef={(el) => {
              scrollerRef.current = el as HTMLElement
            }}
            atTopStateChange={handleAtTopStateChange}
            atTopThreshold={100}
            totalListHeightChanged={() => {
              handleListRendered()
            }}
            initialScrollTop={Number.MAX_SAFE_INTEGER}
            components={{
              Header: () => (
                <ConversationHeader>
                  <Intro>{t('support.chat-intro-you-started')}</Intro>
                  <DateTime>
                    <Sep />
                    {DateF.format(
                      ticket.createdAt,
                      DATE_FORMAT.HH_mm__dd_MM_yy,
                    )}
                    <Sep />
                  </DateTime>
                </ConversationHeader>
              ),
              List: VirtuosoListInner,
              Scroller: VirtuosoScroller,
            }}
            computeItemKey={(_idx, data) => data.id}
            increaseViewportBy={500}
          />
        )
      },
      Error: () => (
        <Empty>
          {t('common.something_went_wrong_try_again')}
          <ButtonRetry
            variant="soft"
            size="h28"
            radius="rounded"
            iconLeft={<Refresh2Icon />}
            onClick={handleRetry}
          >
            {t('common.retry')}
          </ButtonRetry>
        </Empty>
      ),
    })

  const CmpHeader = isAdmin ? HeaderAdmin : HeaderUser

  return (
    <Root data-admin={isAdmin}>
      <CmpHeader onClose={onClose} ticket={ticket} />
      <MessagesList>
        {AsyncStateN.isLoading(messagesState) ? (
          <span />
        ) : (
          <ConversationalHeading>
            <Headline>
              {ticket.subject} <PushPinIcon size={10} />
            </Headline>
          </ConversationalHeading>
        )}
        {renderMessages()}
        <SupportConversationMessage
          isLoading={AsyncStateN.isLoading(messagesState)}
          onMessageSend={handleMessageSend}
        />
      </MessagesList>
      {typing ? (
        <SupportConversationTyping>
          <DotsLoader height={16} width={16} radius={2} color="gray" />
          {t('support.typing')}
        </SupportConversationTyping>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  height: 390px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;

  &[data-admin='true'] {
    height: 500px;
  }
`

const HeaderUser = styled(SupportDialogHeading)`
  border-bottom: none;
`

const HeaderAdmin = styled(SupportDialogHeadingAdmin)`
  border-bottom: none;
`

const VirtuosoScroller = styled.div`
  overflow-x: hidden;
`

const VirtuosoListInner = styled.div`
  display: grid;
  gap: 10px;
  padding-inline: 10px;
`

const MessagesList = styled.div`
  padding-block-start: 0;

  background: var(--appearanceA-5);
  border-radius: 10px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const Empty = styled.div`
  color: var(--text-8);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  place-self: center;
  justify-self: center;

  display: grid;
  justify-content: center;
  justify-items: center;

  gap: 10px;
  width: 200px;
  text-align: center;
`

const ConversationHeader = styled.div`
  padding-inline: 10px;
  margin-bottom: 6px;
  margin-top: -6px;
`

const ConversationalHeading = styled.div`
  display: grid;
  align-items: center;
  padding-inline: 6px;
  padding-block: 6px 0;
`

const Headline = styled.h2`
  background: var(--backgroundA-1);
  color: var(--text-11);

  border-radius: 4px;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;

  padding: 6px 9px;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px;

  svg {
    color: var(--text-10);
  }
`

const Intro = styled.div`
  color: var(--text-8);

  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;
  text-align: center;
  margin-top: 8px;
`

const DateTime = styled.div`
  color: var(--text-8);

  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;
  margin-top: 4px;

  gap: 10px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-content: center;
  align-items: center;
`

const Sep = styled.span`
  background: var(--background-2);

  width: 100%;
  height: 1px;
`

const ButtonRetry = styled(Button)`
  font-weight: 500;
`

const SupportConversationTyping = styled.div`
  color: var(--text-8);
  background: var(--background-1);
  padding: 1px 4px;
  border-radius: 4px;

  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;

  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px;
  align-items: center;

  position: absolute;
  bottom: 51px;
  left: 10px;
`
